import type { User } from "@domain/Auth";
import type { IUsers, RegisterInput } from "@ports/auth/IUsers";
import type { ITokenStore } from "@ports/storage/ITokenStore";
import type { IImpersonationStore } from "@ports/storage/IImpersonationStore";
import { type Result, ok, err } from "@domain/Result";

// The login/register flows share the same shape: success returns a User; the
// known failure modes are an unauthenticated/unknown user and a bad request
// shape (e.g. duplicate email on register). Anything else throws.
type LoginError = "invalid_credentials" | "user_not_found" | "user_not_registered";
type RegisterError = "email_already_exists" | "invalid_input";
type ChangePasswordError =
	/** Backend's 400 with detail mentioning "current password". */
	| { kind: "wrong_current_password"; detail: string }
	/** Backend's 400 for any other validation issue (or no detail). */
	| { kind: "validation_failed"; detail: string };

interface AuthUseCasesDeps {
	userApi: IUsers;
	tokenStore: ITokenStore;
	impersonationStore: IImpersonationStore;
}

/**
 * Auth use cases bundled together because they share dependencies (a backend
 * `IUsers`, the `ITokenStore`, and the `IImpersonationStore`) and operate on
 * the same conceptual subject — the active session.
 *
 * The browser-wired singleton lives at `composition/auth.ts`. Tests construct
 * their own with the factory + mock ports; the CLI does the same.
 */
export interface AuthUseCases {
	login(username: string, password: string): Promise<Result<User, LoginError>>;
	register(input: RegisterInput): Promise<Result<User, RegisterError>>;
	logout(): void;
	changePassword(current: string, next: string): Promise<Result<void, ChangePasswordError>>;
}

/**
 * Authenticate a user. Translates well-known backend error messages into
 * typed `LoginError` variants; lets transport/5xx errors throw.
 */
async function login(deps: AuthUseCasesDeps, username: string, password: string): Promise<Result<User, LoginError>> {
	try {
		const user = await deps.userApi.login(username, password);
		if (!user) return err("invalid_credentials");
		persistAuthTokens(deps, user.token);
		return ok(user);
	} catch (e: unknown) {
		const variant = mapLoginError(e);
		if (variant !== null) return err(variant);
		throw e;
	}
}

/**
 * Register a new user. The returned User may have `token === NEW_USER_TOKEN`
 * (still pending activation); callers are responsible for routing accordingly.
 *
 * Auth tokens are persisted only when the registration is complete (the user
 * has a real token, not the placeholder `NEW_USER_TOKEN`).
 */
async function register(deps: AuthUseCasesDeps, input: RegisterInput): Promise<Result<User, RegisterError>> {
	try {
		const user = await deps.userApi.register(input);
		if (!user) return err("invalid_input");
		if (user.token && user.token !== "new_user") {
			persistAuthTokens(deps, user.token);
		}
		return ok(user);
	} catch (e: unknown) {
		if (isHttpStatus(e, 409)) return err("email_already_exists");
		throw e;
	}
}

/**
 * Clear every token slot and any active impersonation. Targeted (only
 * keys we own) — does not touch other persisted UI state (saved views,
 * layouts).
 */
function logout(deps: AuthUseCasesDeps): void {
	deps.tokenStore.clearAll();
	deps.impersonationStore.clear();
}

/**
 * Changes the active user's password. Translates the backend's
 * string-detection error responses into typed variants:
 *
 *  - `wrong_current_password` — backend 400 whose `detail` (lowercased)
 *    contains "current password". Surfaced inline next to the current
 *    password field.
 *  - `validation_failed` — any other 400. Surfaced as a form-level error.
 *
 * Non-400 errors (network, 5xx, etc.) propagate; the calling vm
 * forwards them to Oops.handleError.
 */
async function changePassword(
	deps: AuthUseCasesDeps,
	current: string,
	next: string,
): Promise<Result<void, ChangePasswordError>> {
	try {
		await deps.userApi.changePassword(current, next);
		return ok(undefined);
	} catch (e: unknown) {
		if (!isHttpStatus(e, 400)) throw e;
		const detail = extractMessage(e) ?? "";
		if (detail.toLowerCase().includes("current password")) {
			return err({ kind: "wrong_current_password", detail });
		}
		return err({ kind: "validation_failed", detail });
	}
}

export function makeAuthUseCases(deps: AuthUseCasesDeps): AuthUseCases {
	return {
		login: (username, password) => login(deps, username, password),
		register: (input) => register(deps, input),
		logout: () => logout(deps),
		changePassword: (current, next) => changePassword(deps, current, next),
	};
}

// ---- internal helpers ----

/**
 * Persist the three auth-token slots through the `ITokenStore` port.
 * - `idToken` (key: `rogo_id_token`) authenticates subsequent API requests.
 * - `jwt` is read by the React app to decide "is the user logged in."
 * - `refreshToken` is reset to empty so a stale token from a prior session
 *   doesn't linger.
 */
function persistAuthTokens(deps: AuthUseCasesDeps, token: string): void {
	deps.tokenStore.setIdToken(token);
	deps.tokenStore.setJwt(token);
	deps.tokenStore.setRefreshToken("");
}

function mapLoginError(e: unknown): LoginError | null {
	const message = extractMessage(e);
	if (!message) return null;
	switch (message) {
		case "Incorrect username or password":
			return "invalid_credentials";
		case "User does not exist":
			return "user_not_found";
		case "User is not registered":
			return "user_not_registered";
		default:
			return null;
	}
}

function extractMessage(e: unknown): string | undefined {
	if (typeof e !== "object" || e === null) return undefined;
	const errLike = e as { response?: { data?: { detail?: string } }; message?: string };
	return errLike.response?.data?.detail ?? errLike.message;
}

function isHttpStatus(e: unknown, status: number): boolean {
	if (typeof e !== "object" || e === null) return false;
	const errLike = e as { response?: { status?: number } };
	return errLike.response?.status === status;
}
